import { useEffect, useState } from 'react';

/**
 * SQA-28 / SQA-41 — Sales Engine liveness banner.
 *
 * Polls /api/admin/engine-health every 60s and renders a warning banner when
 * the server reports `is_stale=true`. SQA-41: is_stale is driven by the engine
 * LIVENESS HEARTBEAT (now() - last_beat_at > threshold), not ingest recency —
 * so an idle-but-healthy engine no longer false-fires the banner. The server
 * encodes business-hours suppression (weekday 7am-7pm MT); the banner just
 * trusts the flag. When healthy or off-hours the component returns null and
 * takes zero pixels.
 *
 * Network errors fail closed (no banner). The Engine being stale is the
 * exact moment the network call is most likely to fail too; we'd rather
 * miss a banner than spuriously fire one against a DB hiccup.
 */

interface EngineHealthPayload {
  is_stale: boolean;
  // SQA-41 liveness heartbeat (drives is_stale).
  last_heartbeat_at: string | null;
  minutes_since_last_heartbeat: number | null;
  heartbeat_threshold_minutes: number;
  // Ingest recency — informational only.
  last_successful_ingest_at: string | null;
  minutes_since_last_success: number | null;
  stale_threshold_minutes: number;
  recent_failures: Array<{ at: string; error_message: string }>;
  recent_call_volume: { last_24h: number; last_7d: number };
  recent_contact_link_rate: number | null;
}

const POLL_INTERVAL_MS = 60_000;

export function EngineHealthBanner(): JSX.Element | null {
  const [payload, setPayload] = useState<EngineHealthPayload | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchHealth(): Promise<void> {
      try {
        const res = await fetch('/api/admin/engine-health', { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          // 401 (not signed in) or 5xx — fail closed by clearing the banner.
          setPayload(null);
          return;
        }
        const data = (await res.json()) as EngineHealthPayload;
        if (!cancelled) setPayload(data);
      } catch {
        // Network failure (offline, server down). Quiet the banner so we
        // don't conflate "DB unreachable" with "engine cron is dead".
        if (!cancelled) setPayload(null);
      }
    }

    void fetchHealth();
    const interval = window.setInterval(() => {
      void fetchHealth();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  if (!payload || !payload.is_stale) return null;

  const minutes = payload.minutes_since_last_heartbeat;
  const messageDetail = minutes === null
    ? 'has not checked in (no heartbeat on record)'
    : `has not checked in for ${minutes} minute${minutes === 1 ? '' : 's'}`;

  return (
    <div
      role="alert"
      className="border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
    >
      <span className="font-semibold">⚠ Sales Engine</span>
      <span className="ml-1">{messageDetail}.</span>
      <span className="ml-1 text-amber-800">Check Erik's Mac.</span>
      <span className="ml-2 text-xs text-amber-700">
        (threshold: {payload.heartbeat_threshold_minutes} min)
      </span>
    </div>
  );
}
